import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import { PDFDocument } from 'pdf-lib';

const ShippingSync = () => {
    const [thermalFile, setThermalFile] = useState(null);
    const [shippingLabelsFile, setShippingLabelsFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [progress, setProgress] = useState(0);

    const handleThermalFileChange = (e) => {
        if (e.target.files.length > 0) {
            setThermalFile(e.target.files[0]);
            setStatusMessage('');
        }
    };

    const handleShippingLabelsFileChange = (e) => {
        if (e.target.files.length > 0) {
            setShippingLabelsFile(e.target.files[0]);
            setStatusMessage('');
        }
    };

    const extractOrderNumbersFromPDF = async (file, isThermal) => {
        try {
            const fileArrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(fileArrayBuffer);
            const pageCount = pdfDoc.getPageCount();
            
            // Store all occurrences of order numbers with their page indices
            const orderOccurrences = [];
            
            for (let i = 0; i < pageCount; i++) {
                setProgress(Math.floor((i / pageCount) * 50));
                const pageText = await extractTextFromPage(pdfDoc, i);
                const orderNumber = extractOrderNumber(pageText, isThermal);
                
                if (orderNumber) {
                    // Store each occurrence with its page index
                    orderOccurrences.push({
                        orderNumber,
                        pageIndex: i
                    });
                }
            }

            return orderOccurrences;
        } catch (error) {
            console.error('Error extracting order numbers:', error);
            setStatusMessage('Error processing PDF: ' + error.message);
            return [];
        }
    };

    const extractTextFromPage = async (pdfDoc, pageIndex) => {
        // This is a simplified placeholder since pdf-lib doesn't directly support text extraction
        // In a real implementation, you might need to use pdf.js or another library for text extraction
        // For demonstration, we'll return a dummy string
        return `Sample text for page ${pageIndex} #5641 Order`;
    };

    const extractOrderNumber = (text, isThermal) => {
        // Extract order number from text
        // This regex looks for #XXXX where XXXX is a number
        const regex = /#(\d+)/;
        const match = text.match(regex);
        if (match && match[1]) {
            return match[1];
        }
        return null;
    };

    const mergePDFs = async () => {
        if (!thermalFile || !shippingLabelsFile) {
            setStatusMessage('Please upload both thermal export and shipping labels PDFs');
            return;
        }

        setProcessing(true);
        setStatusMessage('Processing PDFs...');
        setProgress(0);

        try {
            // Extract order occurrences from both PDFs
            const thermalOrderOccurrences = await extractOrderNumbersFromPDF(thermalFile, true);
            const shippingOrderOccurrences = await extractOrderNumbersFromPDF(shippingLabelsFile, false);

            // Create a new merged PDF
            const mergedPdf = await PDFDocument.create();
            
            const thermalPdfBytes = await thermalFile.arrayBuffer();
            const thermalPdf = await PDFDocument.load(thermalPdfBytes);
            
            const shippingPdfBytes = await shippingLabelsFile.arrayBuffer();
            const shippingPdf = await PDFDocument.load(shippingPdfBytes);

            // Group shipping labels by order number
            const shippingLabelsByOrder = {};
            for (const occurrence of shippingOrderOccurrences) {
                if (!shippingLabelsByOrder[occurrence.orderNumber]) {
                    shippingLabelsByOrder[occurrence.orderNumber] = [];
                }
                shippingLabelsByOrder[occurrence.orderNumber].push(occurrence.pageIndex);
            }

            // Group thermal orders by order number
            const thermalOrdersByOrder = {};
            for (const occurrence of thermalOrderOccurrences) {
                if (!thermalOrdersByOrder[occurrence.orderNumber]) {
                    thermalOrdersByOrder[occurrence.orderNumber] = [];
                }
                thermalOrdersByOrder[occurrence.orderNumber].push(occurrence.pageIndex);
            }

            // Get unique order numbers from thermal orders
            const uniqueOrderNumbers = Object.keys(thermalOrdersByOrder);
            
            // Sort order numbers ascending (can be customized if needed)
            uniqueOrderNumbers.sort((a, b) => parseInt(a) - parseInt(b));
            
            // Process each unique order number
            let processedCount = 0;
            const totalOccurrences = thermalOrderOccurrences.length;
            
            for (const orderNumber of uniqueOrderNumbers) {
                // Get all thermal pages for this order
                const thermalPages = thermalOrdersByOrder[orderNumber];
                // Get all shipping labels for this order
                const shippingPages = shippingLabelsByOrder[orderNumber] || [];
                
                // Make sure shipping labels are sorted (if you need a specific order)
                // shippingPages.sort(); // Add custom sort logic if needed
                
                // Process each thermal page
                for (let i = 0; i < thermalPages.length; i++) {
                    // Add thermal page
                    const [thermalPage] = await mergedPdf.copyPages(thermalPdf, [thermalPages[i]]);
                    mergedPdf.addPage(thermalPage);
                    
                    // Add corresponding shipping label if available
                    if (i < shippingPages.length) {
                        const [shippingPage] = await mergedPdf.copyPages(shippingPdf, [shippingPages[i]]);
                        mergedPdf.addPage(shippingPage);
                    }
                    
                    processedCount++;
                    setProgress(50 + Math.floor((processedCount / totalOccurrences) * 50));
                }
                
                // If there are more shipping pages than thermal pages for this order
                if (shippingPages.length > thermalPages.length) {
                    const extraShippingPages = shippingPages.slice(thermalPages.length);
                    for (const pageIndex of extraShippingPages) {
                        const [shippingPage] = await mergedPdf.copyPages(shippingPdf, [pageIndex]);
                        mergedPdf.addPage(shippingPage);
                    }
                    setStatusMessage('Note: Some orders had more shipping labels than thermal pages. All labels have been included.');
                }
            }

            // Check if there are any orders in shipping labels but not in thermal
            let missingOrders = 0;
            for (const orderNumber in shippingLabelsByOrder) {
                if (!thermalOrdersByOrder[orderNumber]) {
                    missingOrders++;
                    // Optionally add the shipping labels for these orders too if desired
                    // for (const pageIndex of shippingLabelsByOrder[orderNumber]) {
                    //     const [shippingPage] = await mergedPdf.copyPages(shippingPdf, [pageIndex]);
                    //     mergedPdf.addPage(shippingPage);
                    // }
                }
            }
            
            if (missingOrders > 0) {
                setStatusMessage(`Note: ${missingOrders} orders from shipping labels did not match any thermal orders. These shipping labels were excluded.`);
            }

            // Save the merged PDF
            const mergedPdfBytes = await mergedPdf.save();
            const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'merged_shipping_and_orders.pdf';
            link.click();

            if (missingOrders === 0) {
                setStatusMessage('PDF successfully created!');
            }
        } catch (error) {
            console.error('Error merging PDFs:', error);
            setStatusMessage('Error merging PDFs: ' + error.message);
        } finally {
            setProcessing(false);
            setProgress(100);
        }
    };

    return (
        <div className="shipping-sync-container">
            <h2>Shipping Sync</h2>
            <p className="description">
                Upload your thermal export PDF and shipping labels PDF to create a merged document with each order followed by its shipping label.
            </p>
            
            <div className="upload-section">
                <div className="file-upload">
                    <h3>Thermal Export PDF</h3>
                    <p>The PDF containing order details exported from the Orders section.</p>
                    <input 
                        type="file" 
                        accept=".pdf" 
                        onChange={handleThermalFileChange}
                        disabled={processing}
                    />
                    {thermalFile && (
                        <div className="file-info">
                            <p>{thermalFile.name}</p>
                        </div>
                    )}
                </div>
                
                <div className="file-upload">
                    <h3>Shipping Labels PDF</h3>
                    <p>The PDF containing shipping labels for your orders.</p>
                    <input 
                        type="file" 
                        accept=".pdf" 
                        onChange={handleShippingLabelsFileChange}
                        disabled={processing}
                    />
                    {shippingLabelsFile && (
                        <div className="file-info">
                            <p>{shippingLabelsFile.name}</p>
                        </div>
                    )}
                </div>
            </div>
            
            <div className="merge-section">
                <button 
                    className="merge-button"
                    onClick={mergePDFs}
                    disabled={!thermalFile || !shippingLabelsFile || processing}
                >
                    {processing ? 'Processing...' : 'Merge PDFs'}
                </button>
                
                {statusMessage && (
                    <div className="status-message">
                        <p>{statusMessage}</p>
                    </div>
                )}
                
                {processing && (
                    <div className="progress-bar-container">
                        <div 
                            className="progress-bar" 
                            style={{ width: `${progress}%` }}
                        ></div>
                    </div>
                )}
            </div>
            
            <div className="instructions">
                <h3>How it works</h3>
                <ol>
                    <li>Export your orders as thermal PDF from the Orders section</li>
                    <li>Upload your shipping labels PDF from your shipping provider</li>
                    <li>Click "Merge PDFs" to create a new document with each order page followed by its corresponding shipping label</li>
                    <li>The order is matched based on the order number found in both documents</li>
                </ol>
            </div>
        </div>
    );
};

export default ShippingSync; 